/**
 * `portfolio` namespace — English canonical source.
 *
 * Owned by the Portfolio team: the `/portfolio` drill-down route, the live
 * holdings table, and the cashflow ledger + record form. Shared panel *titles*
 * (Portfolio value / Allocation / …) are sourced from the `dashboard` namespace;
 * this file holds the portfolio-only copy. `{param}` tokens interpolate via
 * `t(key, { param })`. Keys are dot-free camelCase; `ko/portfolio.ts` mirrors this
 * shape (a missing key is a compile error). Generic verbs (save/close/retry/
 * saving) are NOT re-declared here — call sites reuse `common.*`.
 */
export const portfolio = {
  // ── route chrome ──
  accountsEyebrow: "Accounts",
  description:
    "Value over time, profit-vs-deposit decomposition, allocation, holdings, and cashflows.",

  // ── summary strip ──
  noSnapshotTitle: "No snapshot yet",
  noSnapshotBody:
    "A portfolio snapshot is taken at session boundaries. Values appear once the first one is recorded.",
  totalValue: "Total value",
  asOf: "as of {date}",
  stocks: "Stocks",
  cash: "Cash",
  netDeposits: "Net deposits",
  marketPnlHint: "market P&L",

  // ── manual snapshot ──
  snapshotNow: "Record snapshot now",
  snapshotQueued: "Snapshot queued — refresh in a moment.",

  // ── holdings ──
  holdingsTitle: "Holdings",
  holdingsGateMessage:
    "Connect and verify a Toss Securities credential in Settings to see live positions.",
  openSettings: "Open Settings",
  positionCount: "{count} positions",
  holdingsEmpty: "No open positions in this account.",
  colSymbol: "Symbol",
  colQty: "Qty",
  colAvgCost: "Avg cost",
  colLast: "Last",
  colMarketValue: "Market value",
  colUnrealizedPnl: "Unrealized P&L",
  colToday: "Today",

  // ── cashflow ledger (read-only) ──
  cashflowTitle: "Cashflow history",
  cashflowSubtitle: "Deposits, withdrawals, dividends, and fees",
  cashflowEmpty: "No cashflow events recorded yet.",
  colInBase: "In {currency}",
  badgeExternal: "external",
  badgeInferred: "inferred",

  // ── cashflow ledger column headers ──
  fieldKind: "Kind",
  fieldDate: "Date",
  fieldAmount: "Amount",
  fieldNote: "Note",

  // ── cashflow kinds (badge labels) ──
  kindDeposit: "Deposit",
  kindWithdrawal: "Withdrawal",
  kindDividend: "Dividend",
  kindInterest: "Interest",
  kindFee: "Fee",
  kindTax: "Tax",
} as const;

export type PortfolioKey = keyof typeof portfolio;
